// frontend/src/screens/CartScreen.js

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import clsx from "clsx";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Info,
  Minus,
  PackageCheck,
  Plus,
  PoundSterling,
  ShieldCheck,
  ShoppingBag,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import {
  clearCart,
  decrementCartItem,
  getCart,
  incrementCartItem,
  refreshCart,
  removeCartItem,
  removePriceChangedItems,
  removeUnavailableItems,
  resetCartError,
  resetCartMeta,
  setCartItemQty,
  validateCart,
} from "../../actions/cartActions";

import Meta from "../../components/Meta";
import { getSafeUserMessage } from "../../utils/errorMessages";
import "./CartScreen.css";

const FALLBACK_IMAGE = "https://placehold.co/640x760?text=Academy+Costumes+London";

const BLOCKING_STOCK_STATUSES = new Set([
  "out_of_stock",
  "requested_qty_exceeds_available",
  "shared_stock_exhausted_in_cart",
  "shared_stock_blocked_by_cart",
  "unavailable",
]);

const NON_EDITABLE_STOCK_STATUSES = new Set(["out_of_stock", "unavailable"]);

const BLOCKING_STOCK_CODES = new Set([
  "OUT_OF_STOCK",
  "INSUFFICIENT_STOCK",
  "INSUFFICIENT_SHARED_STOCK",
  "REQUESTED_QTY_EXCEEDS_AVAILABLE",
  "SHARED_STOCK_EXHAUSTED_IN_CART",
  "SHARED_STOCK_BLOCKED_BY_CART",
  "UNAVAILABLE",
]);

const SUCCESS_TOAST_ACTIONS = new Set([
  "REMOVE_UNAVAILABLE",
  "REMOVE_PRICE_CHANGED",
  "REFRESH",
  "CLEAR",
  "REMOVE_ITEM",
  "SET_ITEM_QTY",
  "INCREMENT_ITEM",
  "DECREMENT_ITEM",
  "ADD_ITEM",
]);

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function toWholeQty(value, fallback = 1) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return fallback;
  return n;
}

function clampQty(value, min = 1, max = Number.POSITIVE_INFINITY) {
  const safeMin = Math.max(1, toWholeQty(min, 1));
  const safeMax = Number.isFinite(Number(max))
    ? Math.max(safeMin, Math.floor(Number(max)))
    : Number.POSITIVE_INFINITY;

  return Math.max(safeMin, Math.min(toWholeQty(value, safeMin), safeMax));
}

function normalizeImage(src = "") {
  const safe = String(src || "").trim();
  return safe || FALLBACK_IMAGE;
}

function safeText(value = "") {
  return String(value || "").trim();
}

function normalizeStatus(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeCode(value = "") {
  return safeText(value).toUpperCase();
}

function getStockStatus(item = {}) {
  return normalizeStatus(
    item?.stockStatus ||
      item?.status ||
      item?.unavailableCode ||
      item?.stockStatusCode,
  );
}

function getStockCode(item = {}) {
  return normalizeCode(item?.stockStatusCode || item?.unavailableCode || "");
}

function getStockReason(item = {}) {
  return safeText(
    item?.stockStatusReason ||
      item?.unavailableReason ||
      item?.disabledReason ||
      "",
  );
}

function getRemainingAddableQty(item = {}) {
  return Math.max(0, Math.floor(Number(item?.remainingAddableQty || 0)));
}

function getMaxQty(item = {}) {
  const maxQty = Number(item?.maxQty);
  const availableAtRefresh = Number(item?.availableAtRefresh);
  const qty = Number(item?.qty || 0);

  if (Number.isFinite(maxQty) && maxQty >= 0) return Math.floor(maxQty);

  if (Number.isFinite(availableAtRefresh) && availableAtRefresh >= 0) {
    return Math.floor(availableAtRefresh);
  }

  return Math.max(1, Math.floor(qty || 1));
}

function itemHasBlockingStatus(item = {}) {
  return (
    BLOCKING_STOCK_STATUSES.has(getStockStatus(item)) ||
    BLOCKING_STOCK_CODES.has(getStockCode(item)) ||
    item?.isAvailable === false
  );
}

function itemCanIncrement(item = {}) {
  if (!item?.isAvailable) return false;
  if (item?.priceChanged) return false;
  if (itemHasBlockingStatus(item)) return false;
  if (item?.canIncrement === false) return false;
  if (item?.canAddMore === false) return false;
  if (item?.canAddToCart === false) return false;

  return (
    getRemainingAddableQty(item) > 0 && Number(item?.qty || 0) < getMaxQty(item)
  );
}

function itemCanEditQty(item = {}) {
  if (item?.priceChanged) return false;

  const maxQty = getMaxQty(item);
  if (maxQty < 1) return false;

  const status = getStockStatus(item);

  if (NON_EDITABLE_STOCK_STATUSES.has(status)) return false;

  return Boolean(item?.isAvailable || maxQty >= 1);
}

function buildProductLink(item = {}) {
  const shopKey = encodeURIComponent(safeText(item?.shopKey));
  const identifier = encodeURIComponent(
    safeText(item?.slug || item?.productSlug || item?.productId),
  );

  if (!shopKey || !identifier) return "/shop";

  return `/shop/${shopKey}/product/${identifier}`;
}

function getLineDescriptor(item = {}) {
  const bits = [];

  if (item?.variantName) bits.push(item.variantName);
  if (item?.unitLabel) bits.push(`Per ${item.unitLabel}`);
  if (item?.color) bits.push(item.color);
  if (item?.size) bits.push(item.size);
  if (item?.material) bits.push(item.material);
  if (item?.length) bits.push(item.length);

  return bits.map(safeText).filter(Boolean).join(" · ");
}

function isSharedTextileLine(item = {}) {
  return (
    item?.productType === "textile" &&
    safeText(item?.variantType).toLowerCase() !== "sample"
  );
}

function getLineAlert(item = {}, currency = "GBP") {
  const status = getStockStatus(item);
  const code = getStockCode(item);
  const reason = getStockReason(item);

  if (item?.priceChanged) {
    return {
      tone: "warning",
      icon: PoundSterling,
      title: "Price updated",
      message: `This line changed from ${formatMoney(
        item.previousUnitPrice || 0,
        currency,
      )} to ${formatMoney(item.unitPrice || 0, currency)}.`,
    };
  }

  if (
    status === "shared_stock_blocked_by_cart" ||
    status === "shared_stock_exhausted_in_cart" ||
    code === "INSUFFICIENT_SHARED_STOCK" ||
    status.includes("shared") ||
    /shared|roll stock/i.test(reason)
  ) {
    return {
      tone: "warning",
      icon: AlertCircle,
      title:
        status === "shared_stock_blocked_by_cart"
          ? "This roll stock is shared"
          : "Roll stock reserved in basket",
      message:
        reason ||
        (status === "shared_stock_blocked_by_cart"
          ? "Another cut in your basket is using the same roll stock. Adjust that related basket item to free more fabric."
          : "The available roll stock for this fabric is already reserved in your basket."),
    };
  }

  if (status === "max_in_cart" || code === "MAX_IN_CART") {
    return {
      tone: "info",
      icon: Info,
      title: "Reserved in basket",
      message:
        reason ||
        "You have the available quantity for this option in your basket.",
    };
  }

  if (
    status === "requested_qty_exceeds_available" ||
    code === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    code === "INSUFFICIENT_STOCK"
  ) {
    return {
      tone: "warning",
      icon: AlertCircle,
      title: "Quantity needs adjusting",
      message:
        reason ||
        `This line can currently be held at ${getMaxQty(item)} or less.`,
    };
  }

  if (
    !item?.isAvailable ||
    status === "out_of_stock" ||
    status === "unavailable" ||
    code === "OUT_OF_STOCK" ||
    code === "UNAVAILABLE"
  ) {
    return {
      tone: "danger",
      icon: XCircle,
      title: status === "out_of_stock" ? "Sold out" : "Unavailable",
      message:
        reason ||
        "This item is not available right now. Remove it to continue checkout.",
    };
  }

  const remaining = getRemainingAddableQty(item);

  if (remaining === 0 && Number(item?.qty || 0) >= getMaxQty(item)) {
    return {
      tone: "info",
      icon: Info,
      title: "All available stock reserved",
      message:
        "You have the available quantity for this option in your basket.",
    };
  }

  if (isSharedTextileLine(item) && remaining > 0 && remaining <= 2) {
    return {
      tone: "info",
      icon: Info,
      title: "Low shared roll stock",
      message: `Only ${remaining} more can be added for this textile selection.`,
    };
  }

  if (remaining > 0 && remaining <= 2) {
    return {
      tone: "info",
      icon: Info,
      title: "Low stock",
      message: `Only ${remaining} more can be added for this selection.`,
    };
  }

  return null;
}

function getQtyHelper(item = {}) {
  const status = getStockStatus(item);
  const code = getStockCode(item);
  const reason = getStockReason(item);
  const remaining = getRemainingAddableQty(item);
  const maxQty = getMaxQty(item);
  const qty = Number(item?.qty || 0);

  if (item?.priceChanged) {
    return "Review the updated price before checkout.";
  }

  if (status === "out_of_stock" || status === "unavailable" || maxQty < 1) {
    return reason || "Remove this line to continue checkout.";
  }

  if (status === "shared_stock_blocked_by_cart") {
    return reason || "Another cut in your basket is using the same roll stock.";
  }

  if (status === "shared_stock_exhausted_in_cart") {
    return (
      reason ||
      "The available roll stock for this fabric is already reserved in your basket."
    );
  }

  if (status === "max_in_cart" || code === "MAX_IN_CART") {
    return (
      reason ||
      "You have the available quantity for this option in your basket."
    );
  }

  if (
    status === "requested_qty_exceeds_available" ||
    code === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    code === "INSUFFICIENT_STOCK"
  ) {
    return reason || `Reduce this line to ${maxQty} or less.`;
  }

  if (remaining > 0) {
    return `You can add ${remaining} more.`;
  }

  if (qty >= maxQty) {
    return "All available quantity is already in your basket.";
  }

  return "";
}

function getSharedStockRows(item = {}) {
  const shared = item?.sharedStock || {};
  if (!shared?.isShared) return [];

  const rows = [];

  if (shared.baseMetres != null) {
    rows.push(["Roll stock", `${Number(shared.baseMetres || 0)} m`]);
  }

  if (shared.consumedByThisLineMetres != null) {
    rows.push([
      "This line uses",
      `${Number(shared.consumedByThisLineMetres || 0)} m`,
    ]);
  }

  if (shared.consumedByOtherLinesMetres != null) {
    rows.push([
      "Related basket cuts use",
      `${Number(shared.consumedByOtherLinesMetres || 0)} m`,
    ]);
  }

  if (shared.remainingMetres != null) {
    rows.push(["Roll remaining", `${Number(shared.remainingMetres || 0)} m`]);
  }

  return rows;
}

function getIssueBanner({ hasItems, unavailableCount, priceChangedCount }) {
  if (!hasItems) return null;

  if (unavailableCount > 0 || priceChangedCount > 0) {
    return {
      tone: "warning",
      icon: AlertCircle,
      title: "Your basket needs attention",
      message:
        "Some items have changed availability or price. Resolve these before checkout.",
    };
  }

  return {
    tone: "success",
    icon: CheckCircle2,
    title: "Basket ready",
    message: "Your basket has been checked against live stock.",
  };
}

function normalizeSummary(summary = {}, cart = {}) {
  const cartUi = cart?.ui || {};
  const summaryUi = summary?.ui || {};

  const itemCount = Number(summary?.itemCount ?? cart?.itemCount ?? 0);
  const distinctItemCount = Number(
    summary?.distinctItemCount ?? cart?.distinctItemCount ?? 0,
  );
  const subtotal = Number(summary?.subtotal ?? cart?.subtotal ?? 0);
  const hasUnavailableItems = Boolean(
    summary?.hasUnavailableItems ?? cart?.hasUnavailableItems,
  );
  const hasPriceChanges = Boolean(
    summary?.hasPriceChanges ?? cart?.hasPriceChanges,
  );
  const readyForCheckout = Boolean(
    summary?.readyForCheckout ?? cart?.readyForCheckout,
  );

  const hasItems = Boolean(
    summaryUi?.hasItems ?? cartUi?.hasItems ?? itemCount > 0,
  );

  const unavailableItemCount = Number(
    summaryUi?.unavailableItemCount ??
      cartUi?.unavailableItemCount ??
      (hasUnavailableItems ? 1 : 0),
  );

  const priceChangedItemCount = Number(
    summaryUi?.priceChangedItemCount ??
      cartUi?.priceChangedItemCount ??
      (hasPriceChanges ? 1 : 0),
  );

  const blockingIssueCount = Number(
    summaryUi?.blockingIssueCount ??
      cartUi?.blockingIssueCount ??
      unavailableItemCount + priceChangedItemCount,
  );

  const canCheckout = Boolean(
    summaryUi?.actions?.canCheckout ??
      cartUi?.actions?.canCheckout ??
      (hasItems && readyForCheckout && blockingIssueCount === 0),
  );

  return {
    itemCount,
    distinctItemCount,
    subtotal,
    hasUnavailableItems,
    hasPriceChanges,
    readyForCheckout,
    ui: {
      status:
        summaryUi?.status ||
        cartUi?.status ||
        (canCheckout ? "ready" : hasItems ? "attention" : "empty"),
      hasItems,
      blockingIssueCount,
      unavailableItemCount,
      priceChangedItemCount,
      actions: {
        canCheckout,
        canRemoveUnavailableItems: Boolean(
          summaryUi?.actions?.canRemoveUnavailableItems ??
            cartUi?.actions?.canRemoveUnavailableItems ??
            unavailableItemCount > 0,
        ),
        canRemovePriceChangedItems: Boolean(
          summaryUi?.actions?.canRemovePriceChangedItems ??
            cartUi?.actions?.canRemovePriceChangedItems ??
            priceChangedItemCount > 0,
        ),
      },
    },
  };
}

const EmptyCartState = memo(function EmptyCartState() {
  return (
    <section className="cart-screen__empty">
      <div className="cart-screen__empty-icon">
        <ShoppingBag size={30} />
      </div>

      <h1 className="cart-screen__empty-title">Your basket is empty</h1>

      <p className="cart-screen__empty-copy">
        Explore fabrics, trims and ready-made pieces, then add them here to
        continue to checkout.
      </p>

      <Link to="/" className="cart-screen__empty-cta">
        Continue shopping
        <ArrowRight size={16} />
      </Link>
    </section>
  );
});

const CartSkeleton = memo(function CartSkeleton() {
  return (
    <main className="cart-screen">
      <div className="cart-screen__skeleton-header">
        <span className="cart-screen__skeleton-line cart-screen__skeleton-line--sm" />
        <span className="cart-screen__skeleton-line cart-screen__skeleton-line--lg" />
      </div>

      <section className="cart-screen__layout">
        <div className="cart-screen__skeleton-list">
          {Array.from({ length: 3 }).map((_, index) => (
            <div className="cart-screen__skeleton-line-item" key={index}>
              <div className="cart-screen__skeleton-image" />
              <div className="cart-screen__skeleton-copy">
                <span className="cart-screen__skeleton-line cart-screen__skeleton-line--md" />
                <span className="cart-screen__skeleton-line cart-screen__skeleton-line--sm" />
                <span className="cart-screen__skeleton-line cart-screen__skeleton-line--full" />
              </div>
            </div>
          ))}
        </div>

        <div className="cart-screen__skeleton-summary">
          <span className="cart-screen__skeleton-line cart-screen__skeleton-line--md" />
          <span className="cart-screen__skeleton-line cart-screen__skeleton-line--full" />
          <span className="cart-screen__skeleton-line cart-screen__skeleton-line--full" />
          <span className="cart-screen__skeleton-button" />
          <span className="cart-screen__skeleton-button" />
        </div>
      </section>
    </main>
  );
});

const CartIssueBanner = memo(function CartIssueBanner({
  banner,
  cartBusy,
  pending,
  unavailableCount,
  priceChangedCount,
  canRemoveUnavailable,
  canRemovePriceChanged,
  onRefresh,
  onRemoveUnavailable,
  onRemovePriceChanged,
}) {
  if (!banner) return null;

  const BannerIcon = banner.icon || Info;

  return (
    <section
      className={clsx(
        "cart-screen__banner",
        banner.tone === "warning" && "is-warning",
        banner.tone === "success" && "is-success",
      )}
      aria-live="polite"
    >
      <div className="cart-screen__banner-main">
        <BannerIcon size={19} />

        <div>
          <strong>{banner.title}</strong>
          <p>{banner.message}</p>
        </div>
      </div>

      <div className="cart-screen__banner-actions">
   

        {unavailableCount > 0 ? (
          <button
            type="button"
            className="cart-screen__ghost-button"
            onClick={onRemoveUnavailable}
            disabled={
              cartBusy ||
              !canRemoveUnavailable ||
              Boolean(pending?.cleanupUnavailable)
            }
          >
            <XCircle size={15} />
            <span>Remove unavailable</span>
          </button>
        ) : null}

        {priceChangedCount > 0 ? (
          <button
            type="button"
            className="cart-screen__ghost-button"
            onClick={onRemovePriceChanged}
            disabled={
              cartBusy ||
              !canRemovePriceChanged ||
              Boolean(pending?.cleanupPriceChanged)
            }
          >
            <PoundSterling size={15} />
            <span>Remove price changes</span>
          </button>
        ) : null}
      </div>
    </section>
  );
});

const CartLineItem = memo(function CartLineItem({
  item,
  currency,
  draftQty,
  itemBusy,
  onDraftChange,
  onCommitQty,
  onIncrement,
  onDecrement,
  onRemove,
}) {
  const [showStockDetails, setShowStockDetails] = useState(false);

  const itemId = safeText(item?._id);
  const lineDescriptor = useMemo(() => getLineDescriptor(item), [item]);
  const productLink = useMemo(() => buildProductLink(item), [item]);
  const lineAlert = useMemo(
    () => getLineAlert(item, currency),
    [item, currency],
  );
  const qtyHelper = useMemo(() => getQtyHelper(item), [item]);
  const sharedRows = useMemo(() => getSharedStockRows(item), [item]);

  const maxQty = getMaxQty(item);
  const inputMaxQty = Math.max(1, maxQty);

  const canIncrease = Boolean(itemCanIncrement(item) && !itemBusy);
  const canEditQty = Boolean(itemCanEditQty(item) && !itemBusy);
  const canDecrease = Boolean(Number(item?.qty || 0) > 1 && canEditQty);

  const LineAlertIcon = lineAlert?.icon || Info;

  const handleDraftChange = useCallback(
    (event) => {
      onDraftChange(itemId, event.target.value, inputMaxQty);
    },
    [inputMaxQty, itemId, onDraftChange],
  );

  const handleCommit = useCallback(() => {
    onCommitQty(item);
  }, [item, onCommitQty]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleCommit();
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onDraftChange(itemId, String(item.qty || 1), inputMaxQty);
      }
    },
    [handleCommit, inputMaxQty, item.qty, itemId, onDraftChange],
  );

  const handleImageError = useCallback((event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;

    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = FALLBACK_IMAGE;
  }, []);

  return (
    <article
      className={clsx(
        "cart-screen__line",
        !item?.isAvailable && "has-issue",
        item?.priceChanged && "has-price-change",
      )}
    >
      <Link to={productLink} className="cart-screen__line-image-link">
        <img
          src={normalizeImage(item.image)}
          alt={item.productName || "Product"}
          className="cart-screen__line-image"
          loading="lazy"
          onError={handleImageError}
        />
      </Link>

      <div className="cart-screen__line-body">
        <div className="cart-screen__line-top">
          <div className="cart-screen__line-copy">
            <Link to={productLink} className="cart-screen__line-title">
              {item.productName || "Product"}
            </Link>

            {lineDescriptor ? (
              <div className="cart-screen__line-subtitle">{lineDescriptor}</div>
            ) : null}

            <div className="cart-screen__line-price-row">
              {item.priceChanged && item.previousUnitPrice ? (
                <span className="cart-screen__line-price-old">
                  {formatMoney(item.previousUnitPrice, currency)}
                </span>
              ) : null}

              <span className="cart-screen__line-price">
                {formatMoney(item.unitPrice, currency)}
              </span>

              {item.unitLabel ? (
                <span className="cart-screen__line-unit">
                  per {item.unitLabel}
                </span>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            className="cart-screen__remove-button"
            onClick={() => onRemove(itemId)}
            disabled={itemBusy || !itemId}
            aria-label={`Remove ${item.productName || "item"}`}
          >
            <Trash2 size={16} />
          </button>
        </div>

        {lineAlert ? (
          <div
            className={clsx(
              "cart-screen__line-alert",
              lineAlert.tone === "warning" && "is-warning",
              lineAlert.tone === "danger" && "is-danger",
              lineAlert.tone === "info" && "is-info",
            )}
          >
            <div className="cart-screen__line-alert-icon">
              <LineAlertIcon size={16} />
            </div>

            <div>
              <div className="cart-screen__line-alert-title">
                {lineAlert.title}
              </div>
              <div className="cart-screen__line-alert-copy">
                {lineAlert.message}
              </div>
            </div>
          </div>
        ) : null}

        {sharedRows.length ? (
          <div className="cart-screen__shared">
            <button
              type="button"
              className="cart-screen__shared-toggle"
              onClick={() => setShowStockDetails((prev) => !prev)}
              aria-expanded={showStockDetails}
            >
              <PackageCheck size={14} />
              <span>
                {showStockDetails
                  ? "Hide shared stock details"
                  : "Show shared stock details"}
              </span>
            </button>

            {showStockDetails ? (
              <div className="cart-screen__shared-grid">
                {sharedRows.map(([label, value]) => (
                  <div
                    className="cart-screen__shared-row"
                    key={`${itemId}-${label}`}
                  >
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="cart-screen__line-bottom">
          <div className="cart-screen__qty-block">
            <div className="cart-screen__qty-label">Quantity</div>

            <div className="cart-screen__qty-controls">
              <button
                type="button"
                className="cart-screen__qty-button"
                onClick={() => onDecrement(itemId)}
                disabled={!canDecrease || !itemId}
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>

              <input
                type="text"
                inputMode="numeric"
                value={draftQty}
                className="cart-screen__qty-input"
                disabled={!canEditQty}
                onChange={handleDraftChange}
                onBlur={handleCommit}
                onKeyDown={handleKeyDown}
                aria-label="Cart line quantity"
              />

              <button
                type="button"
                className="cart-screen__qty-button"
                onClick={() => onIncrement(itemId)}
                disabled={!canIncrease || !itemId}
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>

            {qtyHelper ? (
              <div className="cart-screen__qty-note">{qtyHelper}</div>
            ) : null}
          </div>

          <div className="cart-screen__line-total-block">
            <div className="cart-screen__line-total-label">Line total</div>
            <div className="cart-screen__line-total">
              {formatMoney(item.lineTotal, currency)}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
});

const CartSummaryCard = memo(function CartSummaryCard({
  summary,
  currency,
  unavailableCount,
  priceChangedCount,
  checkoutDisabled,
  cartBusy,
  pending,
  validation,
  onCheckout,
  onRefresh,
  onClear,
}) {
  const lineLabel = summary?.distinctItemCount === 1 ? "line" : "lines";

  return (
    <aside className="cart-screen__summary">
      <div className="cart-screen__summary-card">
        <div className="cart-screen__summary-head">
          <div>
            <p>Secure checkout</p>
            <h2>Order summary</h2>
          </div>

          <span>
            {summary?.distinctItemCount || 0} {lineLabel}
          </span>
        </div>

        <div className="cart-screen__summary-rows">
          <div className="cart-screen__summary-row">
            <span>Items</span>
            <strong>{summary?.itemCount || 0}</strong>
          </div>

          <div className="cart-screen__summary-row">
            <span>Subtotal</span>
            <strong>{formatMoney(summary?.subtotal || 0, currency)}</strong>
          </div>
        </div>

        {unavailableCount > 0 || priceChangedCount > 0 ? (
          <div className="cart-screen__summary-issues">
            {unavailableCount > 0 ? (
              <div className="cart-screen__summary-issue">
                <AlertCircle size={15} />
                <span>{unavailableCount} unavailable line(s)</span>
              </div>
            ) : null}

            {priceChangedCount > 0 ? (
              <div className="cart-screen__summary-issue">
                <PoundSterling size={15} />
                <span>{priceChangedCount} price change(s)</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          className={clsx(
            "cart-screen__primary-button",
            checkoutDisabled && "is-disabled",
          )}
          disabled={checkoutDisabled}
          onClick={onCheckout}
        >
          <span>Proceed to checkout</span>
          <ArrowRight size={16} />
        </button>

       

        <div className="cart-screen__summary-trust">
          <div className="cart-screen__trust-item">
            <ShieldCheck size={15} />
            <span>Validated against live stock</span>
          </div>

          <div className="cart-screen__trust-item">
            <ShieldCheck size={15} />
            <span>Shared textile stock checked</span>
          </div>
        </div>
      </div>

      {validation?.issues?.length ? (
        <div className="cart-screen__issues-card">
          <h3>Latest validation</h3>
          <p>{validation.message}</p>

          <div className="cart-screen__issues-list">
            {validation.issues.slice(0, 5).map((issue, index) => (
              <div
                className="cart-screen__issues-list-item"
                key={`${issue.code || "issue"}-${index}`}
              >
                <span>{issue.message}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </aside>
  );
});

export default function CartScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cartState = useSelector((state) => state.cart || {}, shallowEqual);

  const {
    cart,
    summary,
    validation,
    loading,
    error,
    message,
    pending = {},
    lastAction,
  } = cartState;

  const [qtyDrafts, setQtyDrafts] = useState({});
  const lastSuccessToastKeyRef = useRef("");
  const lastErrorToastKeyRef = useRef("");

  useEffect(() => {
    dispatch(getCart());

    return () => {
      dispatch(resetCartError());
      dispatch(resetCartMeta());
    };
  }, [dispatch]);

  const items = useMemo(
    () => (Array.isArray(cart?.items) ? cart.items : []),
    [cart?.items],
  );

  const itemSignature = useMemo(
    () =>
      items
        .map((item) =>
          [
            item?._id,
            item?.qty,
            item?.updatedAt,
            item?.isAvailable,
            item?.priceChanged,
            item?.remainingAddableQty,
            item?.canIncrement,
            item?.canAddMore,
            item?.canAddToCart,
            item?.stockStatus,
            item?.stockStatusCode,
          ].join(":"),
        )
        .join("|"),
    [items],
  );

  useEffect(() => {
    setQtyDrafts(() => {
      const next = {};

      items.forEach((item) => {
        const key = safeText(item?._id);
        if (!key) return;
        next[key] = String(item?.qty || 1);
      });

      return next;
    });
  }, [itemSignature, items]);

  useEffect(() => {
    if (!message) return;

    const toastKey = `${lastAction}:${message}`;
    if (lastSuccessToastKeyRef.current === toastKey) return;

    lastSuccessToastKeyRef.current = toastKey;

    if (SUCCESS_TOAST_ACTIONS.has(lastAction)) {
      toast.success(message);
    }
  }, [message, lastAction]);

  useEffect(() => {
    if (!error) return;

    const toastKey = String(error);
    if (lastErrorToastKeyRef.current === toastKey) return;

    lastErrorToastKeyRef.current = toastKey;
    toast.error(error);
  }, [error]);

  const currency = safeText(cart?.currency || "GBP");
  const hasItems = items.length > 0;

  const resolvedSummary = useMemo(
    () => normalizeSummary(summary, cart),
    [summary, cart],
  );

  const cartBusy = Boolean(loading);

  const unavailableCount = Number(
    resolvedSummary?.ui?.unavailableItemCount || 0,
  );

  const priceChangedCount = Number(
    resolvedSummary?.ui?.priceChangedItemCount || 0,
  );

  const checkoutDisabled = Boolean(
    cartBusy ||
      pending?.validate ||
      !resolvedSummary?.ui?.actions?.canCheckout ||
      !hasItems,
  );

  const issueBanner = useMemo(
    () =>
      getIssueBanner({
        hasItems,
        unavailableCount,
        priceChangedCount,
      }),
    [hasItems, unavailableCount, priceChangedCount],
  );

  const handleQtyDraftChange = useCallback((itemId, value, maxQty) => {
    const safeItemId = safeText(itemId);
    if (!safeItemId) return;

    const sanitized = String(value || "").replace(/[^\d]/g, "");

    if (!sanitized) {
      setQtyDrafts((prev) => ({
        ...prev,
        [safeItemId]: "",
      }));
      return;
    }

    const clamped = clampQty(sanitized, 1, maxQty);

    setQtyDrafts((prev) => ({
      ...prev,
      [safeItemId]: String(clamped),
    }));
  }, []);

  const commitQty = useCallback(
    async (item) => {
      const itemId = safeText(item?._id);
      if (!itemId) return;

      const raw = qtyDrafts[itemId];
      const currentQty = toWholeQty(item?.qty, 1);
      const maxQty = Math.max(1, getMaxQty(item));
      const nextQty = clampQty(raw, 1, maxQty);

      if (!itemCanEditQty(item)) {
        setQtyDrafts((prev) => ({
          ...prev,
          [itemId]: String(currentQty),
        }));
        return;
      }

      if (nextQty === currentQty) {
        setQtyDrafts((prev) => ({
          ...prev,
          [itemId]: String(currentQty),
        }));
        return;
      }

      try {
        await dispatch(
          setCartItemQty({
            itemId,
            qty: nextQty,
          }),
        );
      } catch {
        setQtyDrafts((prev) => ({
          ...prev,
          [itemId]: String(currentQty),
        }));
      }
    },
    [dispatch, qtyDrafts],
  );

  const handleIncrement = useCallback(
    async (itemId) => {
      const safeItemId = safeText(itemId);
      if (!safeItemId) return;

      try {
        await dispatch(incrementCartItem(safeItemId));
      } catch {
        // Reducer + toast effect handle this.
      }
    },
    [dispatch],
  );

  const handleDecrement = useCallback(
    async (itemId) => {
      const safeItemId = safeText(itemId);
      if (!safeItemId) return;

      try {
        await dispatch(decrementCartItem(safeItemId));
      } catch {
        // Reducer + toast effect handle this.
      }
    },
    [dispatch],
  );

  const handleRemove = useCallback(
    async (itemId) => {
      const safeItemId = safeText(itemId);
      if (!safeItemId) return;

      try {
        await dispatch(removeCartItem(safeItemId));
      } catch {
        // Reducer + toast effect handle this.
      }
    },
    [dispatch],
  );

  const handleRefresh = useCallback(async () => {
    try {
      await dispatch(refreshCart());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleClear = useCallback(async () => {
    const confirmed = window.confirm("Clear your entire basket?");
    if (!confirmed) return;

    try {
      await dispatch(clearCart());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleRemoveUnavailable = useCallback(async () => {
    try {
      await dispatch(removeUnavailableItems());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleRemovePriceChanged = useCallback(async () => {
    try {
      await dispatch(removePriceChangedItems());
    } catch {
      // Reducer + toast effect handle this.
    }
  }, [dispatch]);

  const handleProceedToCheckout = useCallback(async () => {
    try {
      const result = await dispatch(validateCart());

      if (result?.ok) {
        navigate("/checkout/shipping");
        return;
      }

      if (result?.empty) {
        toast.info("Your basket is empty.");
        return;
      }

      toast.warn(
        result?.message ||
          "Please resolve basket issues before continuing to checkout.",
      );
    } catch (err) {
      toast.error(
        getSafeUserMessage(
          err?.message,
          "We could not check your basket right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, navigate]);

  if (loading && !cart) {
    return (
      <>
        <Meta title="Your basket | Academy Costumes London" />
        <CartSkeleton />
      </>
    );
  }

  return (
    <main className="cart-screen">
      <Meta
        title="Your basket | Academy Costumes London"
        description="Review your selected pieces before continuing to checkout."
        url="/cart"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Basket", url: "/cart" },
        ]}
      />


      <header className="cart-screen__header cart-screen__header--simple">
        <div className="cart-screen__header-copy">

          <h1 className="cart-screen__title">Basket</h1>

     
        </div>
      </header>

      {!hasItems ? (
        <EmptyCartState />
      ) : (
        <div className="cart-screen__content">
          <CartIssueBanner
            banner={issueBanner}
            cartBusy={cartBusy}
            pending={pending}
            unavailableCount={unavailableCount}
            priceChangedCount={priceChangedCount}
            canRemoveUnavailable={
              resolvedSummary?.ui?.actions?.canRemoveUnavailableItems
            }
            canRemovePriceChanged={
              resolvedSummary?.ui?.actions?.canRemovePriceChangedItems
            }
            onRefresh={handleRefresh}
            onRemoveUnavailable={handleRemoveUnavailable}
            onRemovePriceChanged={handleRemovePriceChanged}
          />

          <section className="cart-screen__layout" aria-label="Basket contents">
            <motion.section
              className="cart-screen__items"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {items.map((item) => {
                const itemId = safeText(item?._id);
                const itemBusy =
                  Boolean(pending?.itemIds?.[itemId]) || cartBusy;
                const draftQty = qtyDrafts[itemId] ?? String(item.qty || 1);

                return (
                  <CartLineItem
                    key={`${item.lineKey || "line"}-${itemId}`}
                    item={item}
                    currency={currency}
                    draftQty={draftQty}
                    itemBusy={itemBusy}
                    onDraftChange={handleQtyDraftChange}
                    onCommitQty={commitQty}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onRemove={handleRemove}
                  />
                );
              })}
            </motion.section>

            <motion.div
              className="cart-screen__summary-shell"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, delay: 0.04, ease: "easeOut" }}
            >
              <CartSummaryCard
                summary={resolvedSummary}
                currency={currency}
                unavailableCount={unavailableCount}
                priceChangedCount={priceChangedCount}
                checkoutDisabled={checkoutDisabled}
                cartBusy={cartBusy}
                pending={pending}
                validation={validation}
                onCheckout={handleProceedToCheckout}
                onRefresh={handleRefresh}
                onClear={handleClear}
              />
            </motion.div>
          </section>
        </div>
      )}
    </main>
  );
}
